import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';

import type { AlertSettingKey, IAlertSettingsResponse, IAlertsHubUpdate } from '../../../alert-kinds/index.js';
import { OWNS_ERROR_FEEDBACK } from '../../hooks/mutationFeedback.js';
import { applySettingUpdate } from '../alertSettings.js';

export interface ISettingChange<U extends IAlertsHubUpdate> {
  key: AlertSettingKey;
  update: U;
}

/**
 * Saves one alert setting the way every settings screen does: the row
 * changes at once (optimistic), and a failure returns the row to the
 * server's state and tells `onFailed` — the platform's way of saying so (a
 * toast, an alert). Every write ends in a refetch — the server may have
 * written more than was asked (a kind's default frequency with its first
 * channel).
 */
export function useOptimisticSettingUpdate<U extends IAlertsHubUpdate>(
  queryKey: QueryKey,
  save: (key: AlertSettingKey, update: U) => Promise<unknown>,
  onFailed: () => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ key, update }: ISettingChange<U>) => save(key, update),
    onMutate: async ({ key, update }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<IAlertSettingsResponse>(queryKey);
      // A row is only ever changed from a screen that has rendered the settings.
      queryClient.setQueryData(queryKey, applySettingUpdate(previous!, key, update));
      return { previous };
    },
    onError: (_error, _change, context) => {
      queryClient.setQueryData(queryKey, context!.previous);
      onFailed();
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
